import { inject, Injectable } from '@angular/core';
import type { Observable} from 'rxjs';
import { map } from 'rxjs/operators';
import type {
  IUser,
  UserResponse,
  UserCreateRequest,
  UserUpdateRequest,
  UserFilterOptions,
  UserStatsResponse,
  SystemRoleListResponse,
  BusinessRoleListResponse,
  MarketListResponse,
  UserListResponse,
  BulkUserCreateRequest
} from '../../shared/models/user.model';
import config from '../../../config';
import { HttpClient } from '@angular/common/http';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class UserManagementService {

  private apiUrl = `${config.API_GATEWAY_ENV_ENDPOINT}`;
  private http = inject(HttpClient);
  private authService = inject(AuthService);
  /**
   * Get all users with optional filtering
   */
  getUsers(filters?: UserFilterOptions): Observable<UserResponse> {
    let url = `${this.apiUrl}/users`;
    const params: string[] = [];

    if (filters) {
      // Add pagination parameters
      if (filters.pageNumber !== undefined) {
        params.push(`page_number=${filters.pageNumber}`);
      }
      if (filters.pageSize !== undefined) {
        params.push(`page_size=${filters.pageSize}`);
      }

      // Add current user's market ID and role code for backend routing
      if (filters.user_market_id !== undefined && filters.user_market_id !== null) {
        params.push(`user_market_id=${filters.user_market_id}`);
      }
      if (filters.role_code) {
        params.push(`role_code=${encodeURIComponent(filters.role_code)}`);
      }

      // Add BU Admin's business unit IDs
      if (filters.bu_ids && filters.bu_ids.length > 0) {
        params.push(`bu_ids=${filters.bu_ids.join(',')}`);
      }

      // Add search term
      if (filters.searchTerm) {
        params.push(`search_term=${encodeURIComponent(filters.searchTerm)}`);
      }

      // Add role IDs filter
      if (filters.role_ids && filters.role_ids.length > 0) {
        params.push(`roles=${filters.role_ids.join(',')}`);
      }

      // Add region IDs filter
      if (filters.region_ids && filters.region_ids.length > 0) {
        params.push(`regions=${filters.region_ids.join(',')}`);
      }

      // Add market IDs filter
      if (filters.market_ids && filters.market_ids.length > 0) {
        params.push(`markets=${filters.market_ids.join(',')}`);
      }

      // Add business unit IDs filter
      if (filters.business_unit_ids && filters.business_unit_ids.length > 0) {
        params.push(`businessUnits=${filters.business_unit_ids.join(',')}`);
      }

      // Add business role IDs filter
      if (filters.business_role_ids && filters.business_role_ids.length > 0) {
        params.push(`business_role_ids=${filters.business_role_ids.join(',')}`);
      }
    }

    // Append query parameters to URL
    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }

    return this.http.get<UserResponse>(url);
  }

  /**
   * Get user by ID
   */
  getUserById(id: number): Observable<IUser | null> {
    return this.http.get<IUser>(`${this.apiUrl}/users/${id}`).pipe(
      map(user => user || null)
    );
  }

  /**
   * Get user by PRID
   */
  getUserByPRID(prid: string): Observable<IUser | null> {
    return this.http.get<IUser>(`${this.apiUrl}/users/prid/${prid}`).pipe(
      map(user => user || null)
    );
  }

  /**
   * Get compliance BP users for a specific market
   */
  getComplianceBpUsersByMarket(marketId: number): Observable<UserListResponse> {
    return this.http.get<UserListResponse>(`${this.apiUrl}/markets/${marketId}/compliance-bp-users`);
  }

  /**
   * Create a new user
   */
  createUser(userRequest: UserCreateRequest): Observable<IUser> {
    // Get current logged-in user's PRID
    const currentUser = this.authService.userDataValue;
    const currentUserPrid = currentUser?.prid || 'system';

    // Build payload with only required fields
    const payload: Record<string, string | boolean | number | number[] | undefined> = {
      prid: userRequest.prid,
      email: userRequest.email,
      first_name: userRequest.first_name,
      last_name: userRequest.last_name,
      is_active: true,
      created_by: currentUserPrid,
      updated_by: 'none'
    };

    // Conditionally add optional fields only if they have values
    if (userRequest.manager_prid) {
      payload['manager_prid'] = userRequest.manager_prid;
    }

    if (userRequest.system_role_id) {
      payload['system_role_id'] = userRequest.system_role_id;
    }

    if (userRequest.market_id) {
      payload['market_id'] = userRequest.market_id;
    }

    // Always send business_unit_ids as an array (even for single values)
    if (userRequest.business_unit_ids && userRequest.business_unit_ids.length > 0) {
      payload['business_unit_ids'] = userRequest.business_unit_ids;
    }

    if (userRequest.business_role_id) {
      payload['business_role_id'] = userRequest.business_role_id;
    }

    return this.http.post<IUser>(`${this.apiUrl}/users`, payload);
  }

  /**
   * Create multiple users in bulk
   */
  bulkCreateUsers(bulkRequest: BulkUserCreateRequest): Observable<{
    created?: {
      user_id: number;
      prid: string;
      email: string;
      first_name: string;
      last_name: string;
    }[];
    failed?: {
      prid: string;
      error: string;
    }[];
    summary: string;
  }> {
    // Get current logged-in user
    const _currentUser = this.authService.userDataValue;
    const profile = this.authService.getProfile();
    const userName = `${profile.givenName} ${profile.familyName}`.trim() || profile.email || 'Unknown User';

    // Process each user in the array
    const processedUsers = bulkRequest.users.map(userRequest => {
      // Build payload with only required fields
      const payload: Record<string, string | boolean | number | number[] | undefined> = {
        prid: userRequest.prid,
        email: userRequest.email,
        first_name: userRequest.first_name,
        last_name: userRequest.last_name,
        is_active: true,
        created_by: userName,
        updated_by: userName
      };

      // Conditionally add optional fields only if they have values
      if (userRequest.manager_prid) {
        payload['manager_prid'] = userRequest.manager_prid;
      }

      if (userRequest.system_role_id) {
        payload['system_role_id'] = userRequest.system_role_id;
      }

      if (userRequest.market_id) {
        payload['market_id'] = userRequest.market_id;
      }

      // Always send business_unit_ids as an array (even for single values)
      if (userRequest.business_unit_ids && userRequest.business_unit_ids.length > 0) {
        payload['business_unit_ids'] = userRequest.business_unit_ids;
      }

      if (userRequest.business_role_id) {
        payload['business_role_id'] = userRequest.business_role_id;
      }

      return payload;
    });

    return this.http.post<{
      created?: {
        user_id: number;
        prid: string;
        email: string;
        first_name: string;
        last_name: string;
      }[];
      failed?: {
        prid: string;
        error: string;
      }[];
      summary: string;
    }>(
      `${this.apiUrl}/users/bulk`,
      { users: processedUsers }
    );
  }

  /**
   * Update an existing user
   */
  updateUser(userRequest: UserUpdateRequest): Observable<IUser> {
    // Get current logged-in user's PRID
    const currentUser = this.authService.userDataValue;
    const currentUserPrid = currentUser?.prid || 'system';

    // Exclude created_by from update payload
    const { created_by: _created_by, ...updateData } = userRequest;

    // Remove undefined and null values from payload
    const cleanedData = Object.entries(updateData).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null) {
        // Also check for empty arrays
        if (Array.isArray(value) && value.length === 0) {
          return acc; // Skip empty arrays
        }
        acc[key] = value;
      }
      return acc;
    }, {} as Record<string, unknown>);

    const payload = {
      ...cleanedData,
      updated_at: new Date().toISOString(),
      updated_by: currentUserPrid // Send current user's PRID
    };

    return this.http.put<IUser>(`${this.apiUrl}/users/${userRequest.id}`, payload);
  }

  /**
   * Delete a user
   * @param id - User ID to delete
   * @param systemRoleId - Optional system role ID
   * @param marketId - Optional market ID (for non-system admin users)
   */
  deleteUser(id: number, systemRoleId?: number, marketId?: number, deletedBy?: string): Observable<boolean> {
    // Build query params
    const params: { system_role_id?: string; market_id?: string; deleted_by?: string } = {};

    if (systemRoleId !== undefined && systemRoleId !== null) {
      params.system_role_id = systemRoleId.toString();
    }

    // Add market_id for non-system admin users
    if (marketId !== undefined && marketId !== null) {
      params.market_id = marketId.toString();
    }

    // Add deleted_by to track who deleted the user
    if (deletedBy) {
      params.deleted_by = deletedBy;
    }

    return this.http.delete<boolean>(`${this.apiUrl}/users/${id}`, { params });
  }

  /**
   * Get user statistics
   * @param marketId Optional market ID for market-specific stats
   */
  getUserStats(marketId?: number): Observable<UserStatsResponse> {
    let url = `${this.apiUrl}/users/stats`;

    // Add market_id as query parameter if provided
    if (marketId !== undefined && marketId !== null) {
      url += `?market_id=${marketId}`;
    }

    return this.http.get<UserStatsResponse>(url);
  }

  /**
   * Get available system roles
   */
  getSystemRoles(): Observable<SystemRoleListResponse> {
    return this.http.get<SystemRoleListResponse>(`${this.apiUrl}/system-roles`);
  }

  /**
   * Get available business roles
   */
  getBusinessRoles(): Observable<BusinessRoleListResponse> {
    return this.http.get<BusinessRoleListResponse>(`${this.apiUrl}/business-roles`);
  }

  /**
   * Get active markets
   */
  getActiveMarkets(): Observable<MarketListResponse> {
    return this.http.get<MarketListResponse>(`${this.apiUrl}/markets/active`);
  }

  /**
   * Get manager list by market and optional business unit(s)
   * @param marketId The market ID
   * @param businessUnitIds Optional business unit ID(s) - can be a single ID, array of IDs, or comma-separated string
   */
  getManagersByMarket(marketId: number, businessUnitIds?: number | number[] | string): Observable<UserListResponse> {
    let url = `${this.apiUrl}/users/market/${marketId}`;
    if (businessUnitIds) {
      let buParam: string;
      if (Array.isArray(businessUnitIds)) {
        // Array of IDs - join with comma
        buParam = businessUnitIds.join(',');
      } else if (typeof businessUnitIds === 'string') {
        // Already a string - use as is
        buParam = businessUnitIds;
      } else {
        // Single number - convert to string
        buParam = String(businessUnitIds);
      }
      url += `?business_unit_id=${buParam}`;
    }
    return this.http.get<UserListResponse>(url);
  }

  /**
   * Update last login timestamp for a user by PRID
   */
  updateLastLogin(prid: string): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/users/prid/${prid}/last-login`, {});
  }
}
